import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiShield, FiLock, FiUsers, FiEye, FiEyeOff } from 'react-icons/fi';
import { getRoles, createRole, updateRole, deleteRole, getPermissions } from '../services/roles';
import { getUsers, assignRole } from '../services/users';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import RoleForm from '../components/Roles/RoleForm';
import AssignRoleModal from '../components/Roles/AssignRoleModal';
import toast from 'react-hot-toast';

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesData, permissionsData, usersData] = await Promise.all([
        getRoles(),
        getPermissions(),
        getUsers({ limit: 100 })
      ]);
      setRoles(rolesData.roles || []);
      setPermissions(permissionsData.permissions || []);
      setUsers(usersData.users || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (roleData) => {
    try {
      await createRole(roleData);
      toast.success('Role created successfully');
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create role');
    }
  };

  const handleUpdate = async (id, roleData) => {
    try {
      await updateRole(id, roleData);
      toast.success('Role updated successfully');
      setEditingRole(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) return;
    try {
      await deleteRole(id);
      toast.success('Role deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete role');
    }
  };

  const handleAssignRole = async (userId, roleId) => {
    try {
      await assignRole(roleId, userId);
      toast.success('Role assigned successfully');
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign role');
    }
  };

  const getPermissionCountByResource = (rolePermissions, resource) => {
    return rolePermissions.filter(p => p.resource === resource).length;
  };

  const groupPermissionsByResource = (rolePermissions) => {
    const grouped = {};
    rolePermissions.forEach(perm => {
      if (!grouped[perm.resource]) {
        grouped[perm.resource] = [];
      }
      grouped[perm.resource].push(perm);
    });
    return grouped;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Roles & Permissions</h1>
          <p className="text-sm text-gray-500 mt-1">Manage user roles and their permissions</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          <FiPlus className="mr-2" /> Create Role
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Roles</p>
              <p className="text-2xl font-bold text-gray-800">{roles.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FiShield className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Permissions</p>
              <p className="text-2xl font-bold text-gray-800">{permissions.length}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FiEye className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">System Roles</p>
              <p className="text-2xl font-bold text-gray-800">{roles.filter(r => r.is_system).length}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FiLock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Roles Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roles.map((role) => {
          const groupedPermissions = groupPermissionsByResource(role.permissions || []);
          const isExpanded = expandedRole === role._id;
          
          return (
            <div key={role._id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg mr-3 ${role.is_system ? 'bg-purple-100' : 'bg-primary-100'}`}>
                      {role.is_system ? (
                        <FiLock className="w-5 h-5 text-purple-600" />
                      ) : (
                        <FiShield className="w-5 h-5 text-primary-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{role.name}</h3>
                      <p className="text-xs text-gray-500 font-mono">{role.code}</p>
                    </div>
                  </div>
                  {!role.is_system && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingRole(role)}
                        className="text-blue-500 hover:text-blue-700 p-1"
                        title="Edit Role"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(role._id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete Role"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-4">{role.description || 'No description'}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>{role.permissions?.length || 0} permissions</span>
                  <button
                    onClick={() => {
                      setSelectedRole(role);
                      setShowAssignModal(true);
                    }}
                    className="flex items-center text-primary-500 hover:text-primary-600"
                  >
                    <FiUsers className="mr-1 w-3 h-3" />
                    Assign Users
                  </button>
                </div>
                
                {/* Permissions Summary */}
                <div className="border-t pt-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {Object.keys(groupedPermissions).slice(0, 4).map(resource => (
                      <span key={resource} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                        {resource.replace('_', ' ')}: {groupedPermissions[resource].length}
                      </span>
                    ))}
                    {Object.keys(groupedPermissions).length > 4 && (
                      <span className="text-xs text-gray-500">+{Object.keys(groupedPermissions).length - 4} more</span>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setExpandedRole(isExpanded ? null : role._id)}
                    className="text-xs text-primary-500 hover:text-primary-600 flex items-center"
                  >
                    {isExpanded ? 'Show less' : 'View all permissions'}
                  </button>
                  
                  {isExpanded && (
                    <div className="mt-3 max-h-64 overflow-y-auto">
                      {Object.entries(groupedPermissions).map(([resource, perms]) => (
                        <div key={resource} className="mb-3">
                          <p className="text-xs font-semibold text-gray-700 capitalize mb-1">
                            {resource.replace('_', ' ')}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {perms.map(perm => (
                              <span key={perm._id} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                                {perm.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Role Form Modal */}
      <RoleForm
        isOpen={showForm || !!editingRole}
        onClose={() => {
          setShowForm(false);
          setEditingRole(null);
        }}
        onSubmit={editingRole ? (data) => handleUpdate(editingRole._id, data) : handleCreate}
        initialData={editingRole}
        permissions={permissions}
      />

      {/* Assign Role Modal */}
      <AssignRoleModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedRole(null);
        }}
        onAssign={handleAssignRole}
        role={selectedRole}
        users={users}
      />
    </div>
  );
}
